import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import { useNavigate } from 'react-router-dom'
import { apiUrl } from '../utils/api'
import {
  GraduationCap,
  PlayCircle,
  Trophy,
  CheckSquare,
  Settings,
  LogOut,
  BookOpen,
  Rocket,
  Sun,
  Moon,
  BadgeCheck,
  Target,
} from 'lucide-react'
import '../styles/dashboard.css'

interface LeaderboardEntry {
  rank: number
  id: string
  fullName: string
  email: string
  role: string
  coursePoints: number
  quizPoints: number
  totalPoints: number
  isCurrentUser: boolean
}

interface Course {
  id: string
  title: string
  summary: string | null
  cover_image_url: string | null
  total_modules: number
  collection_slug: string
  progress_percentage: number
  status: string
  completed_modules: number
  quizzes_passed: number
  total_quiz_modules: number
  all_modules_read?: boolean
  all_quizzes_passed?: boolean
  is_fully_complete?: boolean
}

export default function Dashboard() {
  const { user, token, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      await Promise.all([fetchCourses(), fetchLeaderboard()])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCourses = async () => {
    try {
      const response = await fetch(apiUrl('/api/courses'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch courses')
      }

      const data = await response.json()
      setCourses(Array.isArray(data.courses) ? data.courses : [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load courses')
    }
  }

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(apiUrl('/api/user/leaderboard'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard')
      }

      const data = await response.json()
      setLeaderboard(Array.isArray(data.leaderboard) ? data.leaderboard : [])
    } catch (err) {
      console.error('Leaderboard error:', err)
      // Don't show error, just silently fail
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Derive stats
  const totalCourses = courses.length
  const completedCourses = courses.filter((c) => c.status === 'completed').length
  const inProgressCourses = courses.filter((c) => c.progress_percentage > 0 && c.status !== 'completed').length
  const totalModulesDone = courses.reduce((sum, c) => sum + c.completed_modules, 0)
  const totalQuizzesPassed = courses.reduce((sum, c) => sum + (c.quizzes_passed || 0), 0)
  const totalQuizModules = courses.reduce((sum, c) => sum + (c.total_quiz_modules || 0), 0)

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-brand">
            <div className="header-logo-icon"><GraduationCap size={20} /></div>
            <div>
              <div className="header-greeting">Welcome back</div>
              <h1>{user?.fullName}</h1>
            </div>
          </div>
          <div className="user-info">
            <div className="user-details">
              <span className="role-badge">{user?.role}</span>
              {user?.publisher && (
                <span className="publisher-badge">{user.publisher}</span>
              )}
            </div>
            {user?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="admin-btn">
                <Settings size={14} /> Admin
              </button>
            )}
            <button className="theme-toggle-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        {/* Stats bar */}
        <div className="stats-bar">
          <div className="stat-card">
            <div className="stat-icon"><BookOpen size={22} /></div>
            <div className="stat-value">{totalCourses}</div>
            <div className="stat-label">Courses Assigned</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><PlayCircle size={22} /></div>
            <div className="stat-value">{inProgressCourses}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><Trophy size={22} /></div>
            <div className="stat-value">{completedCourses}</div>
            <div className="stat-label">Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-icon"><CheckSquare size={22} /></div>
            <div className="stat-value">{totalModulesDone}</div>
            <div className="stat-label">Modules Done</div>
          </div>
          {totalQuizModules > 0 && (
            <div className="stat-card">
              <div className="stat-icon"><BadgeCheck size={22} /></div>
              <div className="stat-value">{totalQuizzesPassed}<span className="stat-value-denom">/{totalQuizModules}</span></div>
              <div className="stat-label">Quizzes Passed</div>
            </div>
          )}
        </div>

        <section className="courses-section">
          <div className="section-heading">
            <h2>Your Training Plan</h2>
            <span className="section-count">{totalCourses} course{totalCourses !== 1 ? 's' : ''}</span>
          </div>

          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="loading">Loading your courses…</div>
          ) : courses.length === 0 ? (
            <div className="no-courses">
              <div className="no-courses-icon"><BookOpen size={36} strokeWidth={1.5} /></div>
              <p>No courses assigned yet. Check back soon!</p>
            </div>
          ) : (
            <div className="courses-grid">
              {courses.map((course) => (
                <div
                  key={course.id}
                  className="course-card"
                  onClick={() => navigate(`/course/${course.id}`)}
                >
                  <div
                    className="course-cover"
                    style={
                      course.cover_image_url
                        ? { backgroundImage: `url(${course.cover_image_url})` }
                        : undefined
                    }
                  >
                    {!course.cover_image_url && <span className="no-cover">No cover</span>}
                    {course.is_fully_complete ? (
                      <span className="cover-status completed-badge">
                        <span className="badge-checkmark">✓</span>
                        <span className="badge-text">COMPLETED</span>
                      </span>
                    ) : (
                      <span className={`cover-status ${course.status}`}>
                        {course.status.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                  <div className="course-card-body">
                    <h3>{course.title}</h3>
                    {course.summary && <p className="course-summary">{course.summary}</p>}
                    <div className="course-meta">
                      <span className="modules-chip">{course.total_modules} modules</span>
                    </div>
                    <div className="course-info">
                      <div className="progress-header">
                        <span className="progress-label">Progress</span>
                        <span className="progress-pct">{course.progress_percentage}%</span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${course.progress_percentage}%` }}
                        />
                      </div>
                      <p className="progress-text">
                        {course.completed_modules} of {course.total_modules} modules complete
                      </p>
                      {course.total_quiz_modules > 0 && (
                        <div className="quiz-pass-row">
                          <div className="quiz-pass-header">
                            <span className="progress-label">Quizzes Passed</span>
                            <span className="quiz-pass-count">{course.quizzes_passed || 0}</span>
                          </div>
                          <div className="quiz-pass-bar">
                            <div
                              className="quiz-pass-fill"
                              style={{ width: `${Math.round(((course.quizzes_passed || 0) / course.total_quiz_modules) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className={`continue-btn ${course.progress_percentage === 0 ? 'start' : ''}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/course/${course.id}`) }}
                    >
                      {course.progress_percentage > 0
                        ? <><PlayCircle size={15} /> Continue Learning</>
                        : <><Rocket size={15} /> Start Course</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Leaderboard section */}
        {leaderboard.length > 0 && (
          <section className="leaderboard-section">
            <div className="section-heading">
              <div className="heading-left">
                <Target size={20} />
                <h2>Publisher Leaderboard</h2>
              </div>
            </div>
            <div className="leaderboard-container">
              <div className="leaderboard-table">
                <div className="leaderboard-header">
                  <div className="leaderboard-rank">Rank</div>
                  <div className="leaderboard-name">Name</div>
                  <div className="leaderboard-courses">Courses</div>
                  <div className="leaderboard-quizzes">Quizzes</div>
                  <div className="leaderboard-total">Total</div>
                </div>
                {leaderboard.map((entry) => (
                  <div key={entry.id} className={`leaderboard-row ${entry.isCurrentUser ? 'current-user' : ''}`}>
                    <div className="leaderboard-rank">
                      <div className="rank-container">
                        <span className={`rank-badge rank-${entry.rank}`}>
                          {entry.rank}
                        </span>
                      </div>
                    </div>
                    <div className="leaderboard-name">
                      <div className="name-info">
                        <div className="name-row">
                          <span className="user-name">{entry.fullName}</span>
                          {entry.rank === 1 && <span className="champion-tag">Champion</span>}
                        </div>
                        <span className="user-role">{entry.role}</span>
                      </div>
                    </div>
                    <div className="leaderboard-courses">
                      <span className="points-value">{entry.coursePoints}</span>
                      <span className="points-label">pts</span>
                    </div>
                    <div className="leaderboard-quizzes">
                      <span className="points-value">{entry.quizPoints}</span>
                      <span className="points-label">pts</span>
                    </div>
                    <div className="leaderboard-total">
                      <span className="total-points">{entry.totalPoints}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}

